// src/components/Admin/CustomerTree/CustomerTable.tsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  HbrDomTable,
  HbrIcon,
  HbrInput,
  HbrPagination,
  HbrButton,
} from '@harbor/elements-react-standalone';
import { caretRight, pencilSimple, plus, magnifyingGlass, x } from '@harbor/elements/icons';
import { DomTree } from '@harbor/elements/utils';

import { useCustomerTreePage } from '../../../hooks/useCustomerTree';
import { CustomerTreeNode } from '../../../types/customer-tree';
import { useCustomerSearch } from '../../../hooks/useCustomerSearch';
import TABLE_STYLES from '../../Common/TableView/table-styles';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';
import StbSingleSelectWithSearch, {
  StbSingleSelectItem,
} from '../../security-toolbox-ui/StbSingleSelectWithSearch';
import StbTableFilters from '../../Common/TableView/StbTableFilters';
import thTemplate from '../../Common/TableView/th-templates';

import { Column, ColumnPin, ColumnType, getColumnTypeConfig } from '../../../types/table';
import { Filter, UserView, UserViewType } from '../../../types/user';
import { useUpdateCustomerMutation } from '../../../hooks/customer-hooks';
import { defaultColumns, defaultOrderBy } from '../../../support/config/tables';
import { useUserRegistry } from '../../../hooks/useUserRegistry';
import { SfRecordType } from '../../../types/salesforce';
import StbLoader from '../../security-toolbox-ui/StbLoader';

interface CustomerTreeTableProps {
  view: UserView;
  pageIndex: number;
  size: number;
  onPageChange: (pageIndex: number) => void;
}

interface EditableCustomerTreeNode extends CustomerTreeNode {
  edit?: boolean;
  _editing_parent_account_id?: string | null;
  _nodeKey?: string;
}

type PendingAction =
  | { type: 'change-parent'; node: EditableCustomerTreeNode }
  | { type: 'clear-parent'; node: EditableCustomerTreeNode }
  | { type: 'add-child'; parent: EditableCustomerTreeNode; child: EditableCustomerTreeNode }
  | { type: 'attach-child'; targetParent: EditableCustomerTreeNode };

let tree: any;
let renderCounter = 0;

/**
 * Normalizes node data to ensure consistent property names
 */
const normalizeNode = (node: any): CustomerTreeNode => {
  const normalized: CustomerTreeNode = {
    id: node.id || node.Id,
    name: node.name || node.Name,
    parent_account_id: node.parent_account_id,
    parent_account_name: node.parent_account_name,
    parent_resolution_method: node.parent_resolution_method,
    parent_resolution_status: node.parent_resolution_status,
    children: (node.children || []).map((child: any) => normalizeNode(child)),
  };

  // Copy over any additional fields
  Object.keys(node).forEach((key) => {
    if (!['Id', 'Name', 'id', 'name', 'children'].includes(key)) {
      (normalized as any)[key] = node[key];
    }
  });

  return normalized;
};

/**
 * Filters out duplicate roots
 */
const filterDuplicateRoots = (items: CustomerTreeNode[]): CustomerTreeNode[] => {
  const childIds = new Set<string>();

  const collectChildIds = (nodes: CustomerTreeNode[]) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          childIds.add(child.id);
          collectChildIds(node.children!);
        });
      }
    });
  };

  collectChildIds(items);
  return items.filter(item => !childIds.has(item.id));
};

// Generate unique node key - OUTSIDE component to prevent recreating
const generateNodeKey = (node: CustomerTreeNode, parentKey = '', counter: number): string => {
  return `${parentKey}/${node.id}/${counter}`;
};

// Deep clone with unique keys - OUTSIDE component to prevent recreating
const cloneNodeWithKey = (node: CustomerTreeNode, parentKey = '', counter: number): EditableCustomerTreeNode => {
  const nodeKey = generateNodeKey(node, parentKey, counter);
  return {
    ...node,
    _nodeKey: nodeKey,
    edit: false,
    children: (node.children || []).map((child) => cloneNodeWithKey(child, nodeKey, counter)),
  };
};

const CustomerTreeTable: React.FC<CustomerTreeTableProps> = ({
                                                               view,
                                                               pageIndex,
                                                               size,
                                                               onPageChange,
                                                             }) => {
  const {
    updateViewColumns,
    updateViewOrderBy,
  } = useUserRegistry();

  const [visibleColumns, setVisibleColumns] = useState<Column[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [source, setSource] = useState<EditableCustomerTreeNode[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [treeOpen, setTreeOpen] = useState<string[]>([]);
  const [expanded] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [forceRenderKey, setForceRenderKey] = useState(0);

  const [apiFilters, setApiFilters] = useState<Filter[]>([]);
  const [modalAccountSelection, setModalAccountSelection] = useState<StbSingleSelectItem | null>(null);

  const updateCustomer = useUpdateCustomerMutation();
  const { setTerm: setParentSearchTerm, results: parentSearchResults } = useCustomerSearch();

  const apiPage = pageIndex + 1;

  // Initialize DomTree once
  if (!tree) {
    tree = new DomTree({
      setSelection: (list: string[]) => {
        const newList = [...list];
        setSelection(newList);
        return newList;
      },
    });
  }

  const initializeVisibleColumns = useCallback(
    (
      columns: Column[] = defaultColumns[UserViewType.CustomerTree],
      order?: { column: string; order: 'asc' | 'desc' },
    ): Column[] => {
      return [
        {
          id: 'Name',
          name: 'Account Name',
          width: 300,
          columnType: ColumnType.Custom,
          sortable: true,
          order: order?.column === 'Name' ? order.order : undefined,
          reorderable: false,
          pin: ColumnPin.Left,
        },
        ...columns.map((column) => ({
          ...column,
          name: column.name,
          columnType: column.columnType || ColumnType.String,
          order: order?.column === column.id ? order.order : undefined,
        })),
        {
          id: 'parent_account_id',
          name: 'Parent Account ID',
          width: 260,
          columnType: ColumnType.String,
          sortable: false,
        },
        {
          id: 'parent_resolution_method',
          name: 'Resolution Method',
          width: 220,
          columnType: ColumnType.String,
          sortable: false,
        },
        {
          id: 'actions',
          name: 'Actions',
          width: 200,
          columnType: ColumnType.Custom,
          sortable: false,
        },
      ];
    },
    [],
  );

  useEffect(() => {
    const columns = (view?.columns || defaultColumns[UserViewType.CustomerTree]).map((column) =>
      getColumnTypeConfig(column),
    );
    setVisibleColumns(
      initializeVisibleColumns(
        columns,
        view?.orderBy ?? defaultOrderBy[UserViewType.CustomerTree],
      ),
    );
  }, [view, initializeVisibleColumns]);

  useEffect(() => {
    if (view.filters && view.filters.length) {
      setApiFilters(view.filters);
    } else {
      setApiFilters([]);
    }
  }, [view.filters]);

  const apiOrderBy = useMemo(
    () => view.orderBy ?? defaultOrderBy[UserViewType.CustomerTree],
    [view.orderBy],
  );

  const apiColumns = useMemo(
    () => (view.columns ?? defaultColumns[UserViewType.CustomerTree]).map((column) => column.id),
    [view.columns],
  );

  const { data, isLoading, refetch } = useCustomerTreePage({
    page: apiPage,
    size,
    filters: apiFilters,
    orderBy: apiOrderBy,
    columns: apiColumns,
    search: searchTerm,
  });

  const totalRoots = data?.total ?? 0;
  const rawRoots: any[] = useMemo(() => data?.items || [], [data?.items]);

  useLayoutEffect(() => {
    if (isLoading) {
      setIsLoadingRows(true);
    } else {
      const timeout = setTimeout(() => setIsLoadingRows(false), 100);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  // Update source when API data changes
  useEffect(() => {
    if (rawRoots.length === 0) {
      return;
    }

    renderCounter++;

    const normalizedRoots = rawRoots.map(node => normalizeNode(node));
    const filteredRoots = filterDuplicateRoots(normalizedRoots);
    const mappedNodes = filteredRoots.map((node) => cloneNodeWithKey(node, '', renderCounter));

    setSource(mappedNodes);
    setSelectedRowId(null);

    if (tree && mappedNodes.length > 0) {
      tree.setSource(mappedNodes);
    }
  }, [rawRoots]); // Only rawRoots - no other dependencies

  const handleSetViewFilters = (filters: Filter[]) => {
    setApiFilters(filters);
    onPageChange(0);
  };

  const handleSearchChange = (event: CustomEvent<any>) => {
    const val = (event.target as any)?.value ?? '';
    setSearchTerm(val.trim());
    onPageChange(0);
  };

  const handleSetVisibleColumns = (selectedColumns: Column[]) => {
    const updatedColumns = selectedColumns.map((col) => {
      const found = visibleColumns.find((selected) => selected.id === col.id);
      return found ?? getColumnTypeConfig(col);
    });
    setVisibleColumns(updatedColumns);
    updateViewColumns(
      UserViewType.CustomerTree,
      view.id,
      sanitizeColumns(updatedColumns),
    );
  };

  const sanitizeColumns = (columns: Column[]) => {
    return columns
      .filter((col: any) => !['Name', 'parent_account_id', 'parent_resolution_method', 'actions'].includes(col.id))
      .map((col: any) => {
        const cleanedCol = { ...col };
        Object.keys(cleanedCol).forEach((key) => {
          if (typeof cleanedCol[key] === 'function') {
            delete cleanedCol[key];
          }
        });
        return cleanedCol;
      });
  };

  const handleChangePage = (chunk: number) => {
    onPageChange(chunk);
  };

  const handleSort = (e: CustomEvent<any>) => {
    const { order, column } = e.detail;
    if (!column || !order || order === 'none') return;

    updateViewOrderBy(UserViewType.CustomerTree, view.id, {
      column: column.id,
      order,
    });

    const colId = column.id;
    renderCounter++;
    const sortedSource = [...source];
    sortedSource.sort((a, b) => {
      const av = colId === 'Name' ? (a.name || '') : (a as any)[colId] || '';
      const bv = colId === 'Name' ? (b.name || '') : (b as any)[colId] || '';
      if (av < bv) return order === 'asc' ? -1 : 1;
      if (av > bv) return order === 'asc' ? 1 : -1;
      return 0;
    });
    const remappedNodes = sortedSource.map((node) => cloneNodeWithKey(node, '', renderCounter));
    setSource(remappedNodes);
    if (tree) tree.setSource(remappedNodes);
    setForceRenderKey((prev) => prev + 1);
  };

  // Immutable update helper
  const updateNodeById = useCallback(
    (
      nodes: EditableCustomerTreeNode[],
      id: string,
      updater: (node: EditableCustomerTreeNode) => EditableCustomerTreeNode,
    ): EditableCustomerTreeNode[] => {
      return nodes.map((n) => {
        if (n.id === id) {
          return updater({ ...n });
        }
        if (n.children && n.children.length) {
          return {
            ...n,
            children: updateNodeById(n.children as EditableCustomerTreeNode[], id, updater),
          };
        }
        return n;
      });
    },
    [],
  );

  const addChildUnderParent = useCallback(
    (
      nodes: EditableCustomerTreeNode[],
      parentId: string,
      child: EditableCustomerTreeNode,
    ): EditableCustomerTreeNode[] =>
      nodes.map((n) => {
        if (n.id === parentId) {
          const children = (n.children as EditableCustomerTreeNode[]) || [];
          return {
            ...n,
            children: [...children, child],
          };
        }
        if (n.children && n.children.length) {
          return {
            ...n,
            children: addChildUnderParent(n.children as EditableCustomerTreeNode[], parentId, child),
          };
        }
        return n;
      }),
    [],
  );

  const handleConfirmSave = async () => {
    if (!pendingAction) return;

    if (pendingAction.type === 'clear-parent') {
      const node = pendingAction.node;
      const nodeId = node.id;

      renderCounter++;
      const updatedSource = updateNodeById(source, nodeId, (n) => ({
        ...n,
        edit: false,
        parent_account_id: null,
        parent_account_name: null,
        parent_resolution_method: 'Manual Edit',
        parent_resolution_status: 'Cleared',
        _editing_parent_account_id: undefined,
      }));
      setSource(updatedSource);
      if (tree) tree.setSource(updatedSource);

      updateCustomer.mutate(
        {
          id: nodeId,
          parent_account_id: null,
          parent_resolution_method: 'Manual Edit',
          parent_resolution_status: 'Cleared',
        },
        {
          onSuccess: () => {
            setTimeout(() => refetch(), 300);
          },
        },
      );
    }

    if (pendingAction.type === 'change-parent') {
      const node = pendingAction.node;
      const nodeId = node.id;
      const editingParentId = node._editing_parent_account_id ?? null;

      renderCounter++;
      const updatedSource = updateNodeById(source, nodeId, (n) => ({
        ...n,
        edit: false,
        parent_account_id: editingParentId,
        parent_resolution_method: 'Manual Edit',
        parent_resolution_status: editingParentId ? 'success' : 'none',
        _editing_parent_account_id: undefined,
      }));
      setSource(updatedSource);
      if (tree) tree.setSource(updatedSource);

      updateCustomer.mutate(
        {
          id: nodeId,
          parent_account_id: editingParentId,
          parent_resolution_method: 'Manual Edit',
          parent_resolution_status: editingParentId ? 'success' : 'none',
        },
        {
          onSuccess: () => {
            setTimeout(() => refetch(), 300);
          },
        },
      );
    }

    if (pendingAction.type === 'add-child') {
      const { parent, child } = pendingAction;
      renderCounter++;
      const updatedSource = addChildUnderParent(source, parent.id, child);
      setSource(updatedSource);
      if (tree) tree.setSource(updatedSource);

      if (!treeOpen.includes(parent.id)) {
        setTreeOpen((prevOpen) => [...prevOpen, parent.id]);
      }

      updateCustomer.mutate(
        {
          id: child.id,
          parent_account_id: parent.id,
          parent_resolution_method: 'Manual Edit',
          parent_resolution_status: 'success',
        },
        {
          onSuccess: () => {
            setTimeout(() => refetch(), 300);
          },
        },
      );
    }

    if (pendingAction.type === 'attach-child') {
      const { targetParent } = pendingAction;
      const targetParentId = targetParent.id;
      const selectedChildId = modalAccountSelection?.id ?? null;

      if (selectedChildId && selectedChildId !== targetParentId) {
        const selectedChildName = modalAccountSelection?.name ?? selectedChildId;

        renderCounter++;
        const updatedSource = updateNodeById(source, selectedChildId, (n) => ({
          ...n,
          parent_account_id: targetParentId,
          parent_account_name: targetParent.name ?? targetParent.id,
          name: n.name || selectedChildName,
        }));
        setSource(updatedSource);
        if (tree) tree.setSource(updatedSource);

        updateCustomer.mutate(
          {
            id: selectedChildId,
            parent_account_id: targetParentId,
            parent_resolution_method: 'Manual Edit',
            parent_resolution_status: 'success',
          },
          {
            onSuccess: () => {
              setTimeout(() => refetch(), 300);
            },
          },
        );
      }
    }

    setPendingAction(null);
    setModalAccountSelection(null);
    setConfirmOpen(false);
  };

  const filteredSource = useMemo(() => {
    if (!searchTerm) return source;
    const term = searchTerm.toLowerCase();
    return source.filter((root) => {
      const matchSelf =
        root.id.toLowerCase().includes(term) ||
        (root.name || '').toLowerCase().includes(term);
      return matchSelf;
    });
  }, [source, searchTerm]);

  const parentSearchItems: StbSingleSelectItem[] = parentSearchResults.map((r) => ({
    id: r.id,
    name: r.name,
  }));

  const handlePlusClick = (node: EditableCustomerTreeNode) => {
    setPendingAction({ type: 'attach-child', targetParent: node });
    setModalAccountSelection(null);
    setConfirmOpen(true);
  };

  const handleClearParent = (node: EditableCustomerTreeNode) => {
    setPendingAction({ type: 'clear-parent', node });
    setConfirmOpen(true);
  };

  const renderBody = () => {
    if (!filteredSource || filteredSource.length === 0) {
      return null;
    }

    const list: JSX.Element[] = [];

    const renderNode = (node: EditableCustomerTreeNode, level: number) => {
      visibleColumns.forEach((column) => {
        const slot = `row-${node.id}-column-${column.id}`;
        const isOpen = treeOpen.includes(node.id);
        const isSelected = selectedRowId === node.id;

        if (column.id === 'Name') {
          const hasChildren = !!(node.children && node.children.length);
          const baseStyle: React.CSSProperties = {
            paddingLeft: `${hasChildren ? level * 24 : level * 24 + 24}px`,
            cursor: 'pointer',
            backgroundColor: isSelected
              ? 'var(--hbr-color-bg-info-soft, #eef6ff)'
              : undefined,
          };

          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-6 flex items-center"
              style={{ ...baseStyle, display: 'flex', gap: '4px', alignItems: 'center' }}
              onClick={() => setSelectedRowId(node.id)}
            >
              {hasChildren && (
                <HbrButton
                  variant="text"
                  size="xsmall"
                  aria={{ label: 'expandable row' }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setSelectedRowId(node.id);
                    const updatedSource = updateNodeById(source, node.id, (n) => ({ ...n, edit: false }));
                    setSource(updatedSource);
                    if (tree) tree.setSource(updatedSource);
                    setTreeOpen(
                      isOpen
                        ? treeOpen.filter((id) => id !== node.id)
                        : [...treeOpen, node.id],
                    );
                  }}
                >
                  <HbrIcon
                    asset={caretRight}
                    className={
                      isOpen ? 'hbr-css__expandable-btn-expanded' : ''
                    }
                  />
                </HbrButton>
              )}
              {node.name || node.id}
            </div>,
          );
        } else if (column.id === 'parent_account_id') {
          const isEditing = !!node.edit;

          if (!isEditing) {
            list.push(
              <div
                key={slot}
                slot={slot}
                className="min-h-6 flex items-center"
                onClick={() => setSelectedRowId(node.id)}
              >
                {node.parent_account_name || node.parent_account_id || '-'}
              </div>,
            );
          } else {
            const currentValue: StbSingleSelectItem | null =
              node._editing_parent_account_id !== undefined
                ? (node._editing_parent_account_id
                  ? {
                    id: node._editing_parent_account_id,
                    name: node.parent_account_name || node._editing_parent_account_id,
                  }
                  : null)
                : (node.parent_account_id
                  ? {
                    id: node.parent_account_id,
                    name: node.parent_account_name || node.parent_account_id,
                  }
                  : null);

            const handleSelectChange = (item: StbSingleSelectItem | null) => {
              if (item === null && node.parent_account_id) {
                // User clicked X to clear - show confirmation immediately
                // First exit edit mode
                const updatedSource = updateNodeById(source, node.id, (n) => ({
                  ...n,
                  edit: false,
                  _editing_parent_account_id: undefined,
                }));
                setSource(updatedSource);
                if (tree) tree.setSource(updatedSource);

                // Then trigger clear confirmation
                setPendingAction({ type: 'clear-parent', node });
                setConfirmOpen(true);
              } else {
                // Normal selection change
                const newParentId = item ? item.id : null;
                const updatedSource = updateNodeById(source, node.id, (n) => ({
                  ...n,
                  _editing_parent_account_id: newParentId,
                }));
                setSource(updatedSource);
                if (tree) tree.setSource(updatedSource);
              }
            };

            list.push(
              <div
                key={slot}
                slot={slot}
                className="min-h-6 flex items-center"
                onClick={() => setSelectedRowId(node.id)}
              >
                <StbSingleSelectWithSearch
                  value={currentValue}
                  items={parentSearchItems}
                  onSearchTermChange={(term) => setParentSearchTerm(term)}
                  onChange={handleSelectChange}
                  groupBy="group"
                  searchPlaceholder="Search by Name"
                  inline={true}
                />
              </div>,
            );
          }
        } else if (column.id === 'parent_resolution_method') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-6 flex items-center"
              onClick={() => setSelectedRowId(node.id)}
            >
              {node.parent_resolution_method || '-'}
            </div>,
          );
        } else if (column.id === 'actions') {
          const isEditing = !!node.edit;

          if (selectedRowId !== node.id) {
            list.push(
              <div
                key={slot}
                slot={slot}
                className="min-h-6 flex items-center justify-end"
              />,
            );
          } else if (!isEditing) {
            list.push(
              <div
                key={slot}
                slot={slot}
                className="min-h-6 flex items-center justify-end gap-1"
              >
                <HbrButton
                  size="small"
                  variant="outline"
                  onClick={() => handlePlusClick(node)}
                  title="Attach child account"
                >
                  <HbrIcon asset={plus} />
                </HbrButton>

                <HbrButton
                  size="small"
                  variant="outline"
                  onClick={() => {
                    const updatedSource = updateNodeById(source, node.id, (n) => ({
                      ...n,
                      edit: true,
                      _editing_parent_account_id: n.parent_account_id,
                    }));
                    setSource(updatedSource);
                    if (tree) tree.setSource(updatedSource);
                  }}
                  title="Edit parent"
                >
                  <HbrIcon asset={pencilSimple} />
                </HbrButton>
              </div>,
            );
          } else {
            list.push(
              <div
                key={slot}
                slot={slot}
                className="min-h-6 flex items-center justify-end gap-1"
              >
                {node.parent_account_id && (
                  <HbrButton
                    size="small"
                    variant="outline"
                    onClick={() => handleClearParent(node)}
                    title="Clear parent"
                  >
                    <HbrIcon asset={x} />
                  </HbrButton>
                )}
                <HbrButton
                  size="small"
                  onClick={() => {
                    setPendingAction({ type: 'change-parent', node });
                    setConfirmOpen(true);
                  }}
                >
                  Save
                </HbrButton>
                <HbrButton
                  size="small"
                  variant="outline"
                  onClick={() => {
                    const updatedSource = updateNodeById(source, node.id, (n) => ({
                      ...n,
                      edit: false,
                      _editing_parent_account_id: undefined,
                    }));
                    setSource(updatedSource);
                    if (tree) tree.setSource(updatedSource);
                  }}
                >
                  Cancel
                </HbrButton>
              </div>,
            );
          }
        } else {
          const value = (node as any)[column.id];
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-6 flex items-center"
              onClick={() => setSelectedRowId(node.id)}
            >
              {value !== undefined && value !== null ? String(value) : '-'}
            </div>,
          );
        }
      });

      if (treeOpen.includes(node.id) && node.children && node.children.length) {
        node.children.forEach((child) =>
          renderNode(child as EditableCustomerTreeNode, level + 1),
        );
      }
    };

    filteredSource.forEach((root) => renderNode(root, 0));

    return list;
  };

  if (isLoadingRows) {
    return <StbLoader isLoading={isLoadingRows} />;
  }

  const confirmMessage = (() => {
    if (!pendingAction) return 'No pending change.';

    if (pendingAction.type === 'clear-parent') {
      const node = pendingAction.node;
      return `You are about to clear the parent relationship for ${node.name || node.id}. This will set parent_account_id to null, parent_resolution_method to "Manual Edit", and parent_resolution_status to "Cleared".`;
    }

    if (pendingAction.type === 'change-parent') {
      const node = pendingAction.node;
      const newParent =
        node._editing_parent_account_id ?? node.parent_account_id ?? '(none)';
      return `You are about to change the parent of ${node.name || node.id} to ${newParent}. This will set the Resolution Method to "Manual Edit".`;
    }

    if (pendingAction.type === 'add-child') {
      const { parent, child } = pendingAction;
      return `You are about to add a new child (${child.id}) under parent ${parent.id}. This will set the child's Resolution Method to "Manual Edit".`;
    }

    if (pendingAction.type === 'attach-child') {
      const { targetParent } = pendingAction;

      if (!modalAccountSelection) {
        return `Select an account below by searching for its name. Once selected, that account (and its subtree) will be moved under ${targetParent.name || targetParent.id} as a child.`;
      }

      const selectedChildName = modalAccountSelection.name ?? modalAccountSelection.id;
      return `You are about to set the parent of ${selectedChildName} to ${targetParent.name || targetParent.id}. This will move that account (and its subtree) under ${targetParent.name || targetParent.id}.`;
    }

    return 'No pending change.';
  })();

  const renderHeader = () =>
    visibleColumns.map((column, index) => (
      <React.Fragment key={`header-${column.id}-${index}`}>
        {thTemplate(column, visibleColumns, SfRecordType.Customer, handleSetVisibleColumns)}
      </React.Fragment>
    ));

  return (
    <>
      <div>
        <div className={`${TABLE_STYLES.filtersGrid} ${TABLE_STYLES.filtersCust}`}>
          <div className="max-w-[672px] h-full flex items-center">
            <HbrInput
              placeholder="Search accounts"
              value={searchTerm}
              size="large"
              clearable
              onHbr-change={handleSearchChange}
              className="mt-2 ml-0 lg:mt-0.5 w-full"
            >
              <HbrIcon
                slot="prefix"
                name="magnifying-glass"
                asset={magnifyingGlass}
                size="16px"
                staticColors={true}
              />
            </HbrInput>
          </div>
          <div />
          <div className="md:col-span-2 xl:col-span-1">
            <StbTableFilters
              userView={view}
              setViewFilters={handleSetViewFilters}
              visibleColumns={visibleColumns}
            />
          </div>
        </div>

        {filteredSource?.length > 0 ? (
          <HbrDomTable
            key={forceRenderKey}
            tree={true}
            autoLayout={true}
            expanded={expanded}
            opened={treeOpen}
            selected={selection}
            columns={visibleColumns}
            data={filteredSource}
            onHbr-sort={handleSort}
          >
            {renderHeader()}
            {renderBody()}
          </HbrDomTable>
        ) : (
          !isLoadingRows && (
            <div className="p-8 text-center">
              <span className="hbr-css__text-secondary-sm">No accounts found</span>
            </div>
          )
        )}

        <div className={`${TABLE_STYLES.pagination} py-2 mb-0`}>
          <HbrPagination
            itemCount={totalRoots}
            pageSize={size}
            page={pageIndex}
            sizeChanger={false}
            onHbr-page-changed={(e: any) => handleChangePage(e.detail)}
          />
        </div>
      </div>

      <StbApiModal
        open={confirmOpen}
        role="update"
        title="Confirm hierarchy change"
        submitLabel="Confirm"
        onSubmit={handleConfirmSave}
        onClose={async () => {
          setConfirmOpen(false);
          setPendingAction(null);
          setModalAccountSelection(null);
        }}
        showResultSection={false}
        suppressResponses={true}
      >
        <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p>{confirmMessage}</p>

          {pendingAction?.type === 'attach-child' && (
            <div style={{ marginTop: 8 }}>
              <label className="block text-sm font-medium mb-1">
                Account to attach as child
              </label>
              <StbSingleSelectWithSearch
                value={modalAccountSelection}
                items={parentSearchItems}
                onSearchTermChange={(term) => setParentSearchTerm(term)}
                onChange={(item) => setModalAccountSelection(item)}
                groupBy="group"
                searchPlaceholder="Search by Name"
                inline={false}
              />
            </div>
          )}
        </div>
      </StbApiModal>
    </>
  );
};

export default CustomerTreeTable;